/**
 * CommissionPane — clean navy/gold commission editor
 * Replaces the old CommissionDisbursementAuthorization Bootstrap modal.
 * Used in both broker-portal TransactionDetails and broker-intelligence TransactionsTab.
 */

import React, { useState, useEffect } from 'react';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import axiosInstance from '../../helpers/api';

const colors = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A',
  white: '#FFFFFF', off: '#E8EEF6', offBg: '#eef1f6', card: '#f8f9fa',
  muted: '#8FA3BF', border: '#e9ecef',
  green: '#27ae60', red: '#e74c3c',
};
const fonts = { body: "'DM Sans', -apple-system, sans-serif" };

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{
    background: colors.white, border: `1px solid ${colors.border}`,
    borderLeft: `4px solid ${colors.gold}`, borderRadius: 8,
    padding: '16px 20px', marginBottom: 12,
  }}>
    <div style={{
      fontSize: 11, fontWeight: 700, color: colors.navy,
      textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10,
    }}>
      {title}
    </div>
    {children}
  </div>
);

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

// PDF Styles
const pdfStyles = StyleSheet.create({
  page: { flexDirection: 'column', backgroundColor: '#ffffff', padding: 30 },
  watermark: { position: 'absolute', fontSize: 60, color: '#e0e0e0', opacity: 0.1, transform: 'rotate(-45deg)', top: '40%', left: '20%' },
  header: { marginBottom: 20, borderBottom: '2px solid #0F1E35', paddingBottom: 10 },
  companyInfo: { fontSize: 10, color: '#666', textAlign: 'right' },
  subtitle: { fontSize: 10, fontWeight: 'bold', color: '#333', marginTop: 10, marginBottom: 5 },
  row: { flexDirection: 'row', marginBottom: 5 },
  label: { fontSize: 8, color: '#666', width: '30%' },
  value: { fontSize: 8, color: '#000', width: '70%' },
  table: { marginTop: 5, marginBottom: 5 },
  tableHeader: { flexDirection: 'row', backgroundColor: '#f0f0f0', padding: 5, borderBottom: '1px solid #ddd' },
  tableRow: { flexDirection: 'row', padding: 5, borderBottom: '1px solid #eee' },
  tableCell: { fontSize: 10, flex: 1 },
  tableCellAmount: { fontSize: 10, width: 100, textAlign: 'right' },
  footer: { position: 'absolute', bottom: 30, left: 30, right: 30, fontSize: 8, color: '#999', textAlign: 'center' },
  instructionsBox: { marginTop: 10, padding: 10, backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: 4 },
  instructionsText: { fontSize: 7, color: '#000', lineHeight: 1.4 },
});

// CDA PDF Document
const CDAPDF: React.FC<{
  transaction: any; brokerageData: any; grossCommission: number; agentGross: number;
  brokerGross: number; agentNet: number; agentSplitPct: number; brokerSplitPct: number;
  commRate: number; salePrice: number; txnFee: number; eoFee: number;
  partners: any[]; miscFees: any[]; specialInstructions: string; tableFunding: boolean;
  grossCalc: number;
}> = ({ transaction, brokerageData, grossCommission, agentGross, brokerGross, agentNet,
  agentSplitPct, brokerSplitPct, commRate, salePrice, txnFee, eoFee,
  partners, miscFees, specialInstructions, tableFunding, grossCalc }) => {

  const propertyAddress = transaction?.property?.address || transaction?.property_data?.address || 'N/A';
  const propertyCity = transaction?.property?.city || transaction?.property_data?.city || '';
  const propertyState = transaction?.property?.state || transaction?.property_data?.state || '';
  const propertyZip = transaction?.property?.zip || transaction?.property_data?.zip || '';
  const fullAddress = propertyCity ? `${propertyAddress}, ${propertyCity}, ${propertyState} ${propertyZip}` : propertyAddress;
  // Determine which side we represent from the agency disclosure / parties
  const parties = transaction?.parties || [];
  const agent = parties.find((p: any) => p.party_type === 'agent');
  const client = parties.find((p: any) => ['buyer', 'seller', 'tenant', 'landlord'].includes(p.party_type));
  const clientSide = client?.party_type || transaction?.type || 'buyer';
  const clientLabel = clientSide === 'seller' || clientSide === 'listing' ? 'Seller (Our Client)' :
                      clientSide === 'tenant' ? 'Tenant (Our Client)' :
                      clientSide === 'landlord' ? 'Landlord (Our Client)' : 'Buyer (Our Client)';

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <Text style={pdfStyles.watermark}>INTERNAL USE ONLY</Text>

        {/* Header */}
        <View style={pdfStyles.header}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <View>
              <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#0F1E35' }}>
                {brokerageData.company_name}
              </Text>
              <Text style={{ fontSize: 12, color: '#C9952A', marginTop: 5, fontWeight: 'bold' }}>
                COMMISSION DISBURSEMENT AUTHORIZATION (CDA)
              </Text>
            </View>
            <View style={pdfStyles.companyInfo}>
              <Text>{brokerageData.company_address}</Text>
              <Text>{brokerageData.company_phone}</Text>
              <Text>{brokerageData.company_email}</Text>
            </View>
          </View>
        </View>

        {/* Transaction + Commission Info Side by Side */}
        <View style={{ flexDirection: 'row', marginBottom: 15 }}>
          <View style={{ flex: 1, paddingRight: 10 }}>
            <Text style={pdfStyles.subtitle}>Transaction Information</Text>
            <View style={pdfStyles.row}><Text style={pdfStyles.label}>Property:</Text><Text style={pdfStyles.value}>{fullAddress}</Text></View>
            <View style={pdfStyles.row}><Text style={pdfStyles.label}>Type:</Text><Text style={pdfStyles.value}>{transaction?.type || 'Purchase'}</Text></View>
            <View style={pdfStyles.row}><Text style={pdfStyles.label}>{clientLabel}:</Text><Text style={pdfStyles.value}>{client?.name || transaction?.client_name || 'N/A'}</Text></View>
            <View style={pdfStyles.row}><Text style={pdfStyles.label}>Agent:</Text><Text style={pdfStyles.value}>{agent?.name || transaction?.agent_name || 'N/A'}</Text></View>
          </View>
          <View style={{ flex: 1, paddingLeft: 10 }}>
            <Text style={pdfStyles.subtitle}>Commission Details</Text>
            <View style={pdfStyles.row}><Text style={pdfStyles.label}>Sale Price:</Text><Text style={pdfStyles.value}>{formatCurrency(salePrice)}</Text></View>
            <View style={pdfStyles.row}><Text style={pdfStyles.label}>Commission Rate:</Text><Text style={pdfStyles.value}>{commRate}%</Text></View>
            <View style={pdfStyles.row}><Text style={pdfStyles.label}>Gross Commission:</Text><Text style={pdfStyles.value}>{formatCurrency(grossCommission)}</Text></View>
            <View style={pdfStyles.row}><Text style={pdfStyles.label}>File #:</Text><Text style={pdfStyles.value}>{transaction?.file_number || 'N/A'}</Text></View>
            <View style={pdfStyles.row}><Text style={pdfStyles.label}>Closing Date:</Text><Text style={pdfStyles.value}>
              {transaction?.estimated_closing_date || transaction?.property_data?.closing_date
                ? new Date(transaction.estimated_closing_date || transaction.property_data.closing_date).toLocaleDateString()
                : 'TBD'}
            </Text></View>
          </View>
        </View>

        {/* Payout Breakdown */}
        <View style={{ marginBottom: 20 }}>
          <Text style={pdfStyles.subtitle}>Final Payout Breakdown</Text>
          <View style={pdfStyles.table}>
            <View style={pdfStyles.tableHeader}>
              <Text style={[pdfStyles.tableCell, { flex: 3 }]}>Recipient</Text>
              <Text style={pdfStyles.tableCellAmount}>Amount</Text>
            </View>
            {/* Agent */}
            <View style={pdfStyles.tableRow}>
              <Text style={[pdfStyles.tableCell, { flex: 3 }]}>{transaction?.agent_name || 'Agent'} ({agentSplitPct}%)</Text>
              <Text style={pdfStyles.tableCellAmount}>{formatCurrency(agentGross)}</Text>
            </View>
            {/* Broker */}
            <View style={pdfStyles.tableRow}>
              <Text style={[pdfStyles.tableCell, { flex: 3 }]}>{brokerageData.company_name} ({brokerSplitPct}%)</Text>
              <Text style={pdfStyles.tableCellAmount}>{formatCurrency(brokerGross)}</Text>
            </View>
            {/* Fees — shown as positive amounts with clear deduction labels */}
            {txnFee > 0 && <View style={pdfStyles.tableRow}><Text style={[pdfStyles.tableCell, { flex: 3 }]}>Transaction Fee (deduct from agent)</Text><Text style={pdfStyles.tableCellAmount}>{formatCurrency(txnFee)}</Text></View>}
            {eoFee > 0 && <View style={pdfStyles.tableRow}><Text style={[pdfStyles.tableCell, { flex: 3 }]}>E&O Insurance (deduct from agent)</Text><Text style={pdfStyles.tableCellAmount}>{formatCurrency(eoFee)}</Text></View>}
            {/* Misc Fees */}
            {miscFees.filter(m => (parseFloat(m.amount) || 0) > 0).map((m, i) => (
              <View key={i} style={pdfStyles.tableRow}>
                <Text style={[pdfStyles.tableCell, { flex: 3 }]}>{m.description || 'Misc Fee'} (deduct from {m.paidBy})</Text>
                <Text style={pdfStyles.tableCellAmount}>{formatCurrency(parseFloat(m.amount) || 0)}</Text>
              </View>
            ))}
            {/* Partners — pay to partner, deduct from agent */}
            {partners.filter(p => (parseFloat(p.amount) || 0) > 0).map((p, i) => {
              const val = parseFloat(p.amount) || 0;
              const amt = p.isPercent ? grossCalc * (val / 100) : val;
              return (
                <View key={i} style={pdfStyles.tableRow}>
                  <Text style={[pdfStyles.tableCell, { flex: 3 }]}>Pay to: {p.name || 'Partner'} — Referral Fee</Text>
                  <Text style={pdfStyles.tableCellAmount}>{formatCurrency(amt)}</Text>
                </View>
              );
            })}
            {/* Agent Net */}
            <View style={[pdfStyles.tableRow, { marginTop: 10, backgroundColor: '#f0f0f0' }]}>
              <Text style={[pdfStyles.tableCell, { flex: 3, fontWeight: 'bold', fontSize: 11 }]}>Agent Net Pay</Text>
              <Text style={[pdfStyles.tableCellAmount, { fontWeight: 'bold', fontSize: 11 }]}>{formatCurrency(agentNet)}</Text>
            </View>
          </View>
        </View>

        {/* Special Instructions */}
        {(specialInstructions || tableFunding) && (
          <View style={{ marginBottom: 20 }}>
            <Text style={pdfStyles.subtitle}>Special Instructions</Text>
            {tableFunding && (
              <View style={pdfStyles.row}>
                <Text style={[pdfStyles.label, { fontWeight: 'bold', color: '#cc0000' }]}>TABLE FUNDING:</Text>
                <Text style={[pdfStyles.value, { fontWeight: 'bold', color: '#cc0000' }]}>Wire transfer required at closing</Text>
              </View>
            )}
            {specialInstructions && (
              <View style={pdfStyles.instructionsBox}><Text style={pdfStyles.instructionsText}>{specialInstructions}</Text></View>
            )}
          </View>
        )}

        {/* Broker Authorization */}
        <View style={{ marginTop: 20, padding: 15, border: '1px solid #333' }}>
          <Text style={[pdfStyles.subtitle, { marginBottom: 10 }]}>Broker Authorization</Text>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 15 }}>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 10, fontWeight: 'bold' }}>{brokerageData.company_name}</Text>
              <Text style={{ fontSize: 9 }}>Attn: Funding Department</Text>
              <Text style={{ fontSize: 9 }}>{brokerageData.company_address}</Text>
              <Text style={{ fontSize: 9 }}>Phone: {brokerageData.company_phone}</Text>
              <Text style={{ fontSize: 9 }}>{brokerageData.company_email}</Text>
            </View>
            <View style={{ flex: 1, alignItems: 'flex-end' }}>
              <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 5 }}>Signature</Text>
              <Text style={{ fontSize: 20, fontFamily: 'Times-Italic', color: '#0F1E35', marginBottom: 5, borderBottom: '1px solid #333', paddingBottom: 2, width: 150, textAlign: 'center' }}>
                Michael Boykin
              </Text>
              <Text style={{ fontSize: 9 }}>Name: Michael Boykin</Text>
              <Text style={{ fontSize: 9 }}>Broker of Record</Text>
              <Text style={{ fontSize: 9 }}>Date: {new Date().toLocaleDateString()}</Text>
              <Text style={{ fontSize: 9 }}>Ref / Inv #: {transaction?.file_number || 'N/A'}</Text>
            </View>
          </View>
        </View>

        <Text style={pdfStyles.footer}>
          Created {new Date().toLocaleDateString()} {'\u2022'} {new Date().toLocaleTimeString()} {'\u2022'}
          {'\u00A9'} {new Date().getFullYear()} {brokerageData.company_name} {'\u2022'}
          Not Valid for Commission Authorization - Internal Use Only
        </Text>
      </Page>
    </Document>
  );
};

interface CommissionPaneProps {
  transaction: any;
  onReload?: () => void;
  portal?: 'agent' | 'broker';
}

const CommissionPane: React.FC<CommissionPaneProps> = ({ transaction, onReload, portal = 'agent' }) => {
  const isBroker = portal === 'broker';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [commType, setCommType] = useState('percentage');
  const [splitType, setSplitType] = useState('percentage');
  const [f, setF] = useState<Record<string, string>>({});

  const set = (key: string, val: string) => setF(prev => ({ ...prev, [key]: val }));
  const num = (key: string) => parseFloat(f[key] || '0') || 0;
  const fmt = (n: number) => '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const txnId = transaction?.id;

  useEffect(() => {
    if (!txnId) return;
    (async () => {
      try {
        const res = await axiosInstance.get(`/transaction-closing/transactions/${txnId}/commission-overrides`);
        const o = res.data?.override;
        if (o) {
          setCommType(o.commission_type || 'percentage');
          setSplitType(o.split_type || 'percentage');
          setSpecialInstructions(o.special_instructions || '');
          setTableFunding(!!o.is_table_funding);
          setApprovalStatus(o.approval_status || 'draft');
          try {
            const savedMisc = typeof o.misc_fees === 'string' ? JSON.parse(o.misc_fees) : o.misc_fees;
            // Handle old format (array of fees) and new format ({ fees, partners })
            const feesArr = Array.isArray(savedMisc) ? savedMisc : savedMisc?.fees || [];
            const partnersArr = savedMisc?.partners || [];
            if (feesArr.length > 0) setMiscFees(feesArr.map((m: any) => ({ id: String(Date.now() + Math.random()), description: m.description || '', amount: String(m.amount || 0), paidBy: m.paidBy || 'agent' })));
            if (partnersArr.length > 0) setPartners(partnersArr.map((p: any) => ({ id: String(Date.now() + Math.random()), name: p.name || '', amount: String(p.amount || 0), isPercent: p.isPercent !== false })));
          } catch {}
          setF({
            sale_price: String(parseFloat(o.sale_price) || transaction.property_data?.price || transaction.property?.price || 0),
            commission_rate: String(parseFloat(o.commission_rate) || 3),
            flat_fee_amount: String(parseFloat(o.flat_fee_amount) || 0),
            agent_split_pct: String(parseFloat(o.agent_split_percentage) || 80),
            agent_flat_amount: String(parseFloat(o.agent_flat_amount) || 0),
            broker_flat_amount: String(parseFloat(o.broker_flat_amount) || 0),
            transaction_fee: String(parseFloat(o.transaction_fee) || 0),
            eo_fee: String(parseFloat(o.eo_fee) || 0),
            monthly_fee: String(parseFloat(o.monthly_fee) || 0),
          });
        } else {
          const price = transaction.property_data?.price || transaction.property?.price || 0;
          setF({
            sale_price: String(price),
            commission_rate: '3',
            flat_fee_amount: '0',
            agent_split_pct: '80',
            agent_flat_amount: '0',
            broker_flat_amount: '0',
            transaction_fee: '395',
            eo_fee: '60',
            monthly_fee: '0',
          });
        }
      } catch {}
      setLoading(false);
    })();
  }, [txnId]);

  // Partner splits, misc fees, instructions
  const [partners, setPartners] = useState<{ id: string; name: string; amount: string; isPercent: boolean }[]>([]);
  const [miscFees, setMiscFees] = useState<{ id: string; description: string; amount: string; paidBy: string }[]>([]);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [tableFunding, setTableFunding] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<'draft' | 'submitted' | 'approved'>('draft');
  const [generatingCDA, setGeneratingCDA] = useState(false);

  // Live calculations
  const grossCommission = commType === 'flat_fee'
    ? num('flat_fee_amount')
    : num('sale_price') * (num('commission_rate') / 100);
  const agentSplitPct = num('agent_split_pct');
  const brokerSplitPct = 100 - agentSplitPct;
  const BROKER_MIN_COMMISSION = 695;
  const rawAgentGross = splitType === 'percentage'
    ? grossCommission * (agentSplitPct / 100)
    : num('agent_flat_amount');
  const rawBrokerGross = splitType === 'percentage'
    ? grossCommission * (brokerSplitPct / 100)
    : num('broker_flat_amount');
  // Enforce $695 minimum broker commission
  const brokerGross = Math.max(rawBrokerGross, grossCommission > 0 ? BROKER_MIN_COMMISSION : 0);
  const agentGross = grossCommission - brokerGross;
  const totalPartnerSplits = partners.reduce((sum, p) => {
    const val = parseFloat(p.amount) || 0;
    return sum + (p.isPercent ? grossCommission * (val / 100) : val);
  }, 0);
  const totalMiscFees = miscFees.filter(m => m.paidBy === 'agent').reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);
  const agentNet = agentGross - num('transaction_fee') - num('eo_fee') - totalPartnerSplits - totalMiscFees;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.post(`/transaction-closing/transactions/${txnId}/commission-overrides`, {
        commission_type: commType,
        sale_price: num('sale_price'),
        commission_rate: num('commission_rate'),
        flat_fee_amount: num('flat_fee_amount'),
        split_type: splitType,
        agent_split_percentage: agentSplitPct,
        broker_split_percentage: brokerSplitPct,
        agent_flat_amount: num('agent_flat_amount'),
        broker_flat_amount: num('broker_flat_amount'),
        transaction_fee: num('transaction_fee'),
        eo_fee: num('eo_fee'),
        monthly_fee: num('monthly_fee'),
        gross_commission: grossCommission,
        agent_gross_commission: agentGross,
        broker_gross_commission: brokerGross,
        agent_net_commission: agentNet,
        broker_net_commission: brokerGross,
        misc_fees: {
          fees: miscFees.map(m => ({ description: m.description, amount: parseFloat(m.amount) || 0, paidBy: m.paidBy })),
          partners: partners.map(p => ({ name: p.name, amount: parseFloat(p.amount) || 0, isPercent: p.isPercent })),
        },
        special_instructions: specialInstructions,
        is_table_funding: tableFunding,
      });
      onReload?.();
    } catch {}
    setSaving(false);
  };

  const field = (label: string, key: string, prefix?: string, suffix?: string, opts?: { disabled?: boolean }) => (
    <div>
      <div style={{ fontSize: 10, color: colors.muted, marginBottom: 3 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', background: colors.white, border: `1px solid ${colors.border}`, borderRadius: 5, overflow: 'hidden' }}>
        {prefix && <span style={{ padding: '6px 8px', background: colors.offBg, color: colors.navy, fontSize: 12, fontWeight: 600, borderRight: `1px solid ${colors.border}` }}>{prefix}</span>}
        <input
          type="text"
          inputMode="decimal"
          value={opts?.disabled ? String(brokerSplitPct) : (f[key] ?? '')}
          onChange={(e) => !opts?.disabled && set(key, e.target.value)}
          disabled={opts?.disabled}
          placeholder="0"
          style={{
            flex: 1, border: 'none', outline: 'none', padding: '6px 10px',
            fontSize: 13, fontFamily: fonts.body, color: colors.navy,
            background: opts?.disabled ? colors.offBg : 'transparent',
          }}
        />
        {suffix && <span style={{ padding: '6px 8px', background: colors.offBg, color: colors.navy, fontSize: 12, fontWeight: 600, borderLeft: `1px solid ${colors.border}` }}>{suffix}</span>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <SectionCard title="Commission">
        <div style={{ padding: 20, textAlign: 'center', color: colors.muted, fontSize: 12 }}>Loading...</div>
      </SectionCard>
    );
  }

  const selectStyle: React.CSSProperties = {
    padding: '6px 10px', border: `1px solid ${colors.border}`,
    borderRadius: 5, fontSize: 12, fontFamily: fonts.body, color: colors.navy,
    background: colors.white, outline: 'none',
  };

  return (
    <div style={{ fontFamily: fonts.body }}>
      {/* Live Summary — compact strip */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 12,
        background: colors.white, border: `1px solid ${colors.border}`, borderRadius: 8,
        padding: '10px 16px',
      }}>
        {[
          { label: 'Gross', value: fmt(grossCommission), color: colors.navy },
          { label: 'Agent', value: fmt(agentGross), color: colors.navy },
          { label: 'Agent Net', value: fmt(agentNet), color: colors.green },
          { label: 'Broker', value: fmt(brokerGross), color: colors.gold },
        ].map((s) => (
          <div key={s.label} style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: 9, color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* All fields in one card */}
      <SectionCard title="Commission">
        {/* Row 1: Type + Rate/Price */}
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr', gap: 10, marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 10, color: colors.muted, marginBottom: 3 }}>Type</div>
            <select value={commType} onChange={(e) => setCommType(e.target.value)} style={{ ...selectStyle, width: '100%' }}>
              <option value="percentage">Percentage</option>
              <option value="flat_fee">Flat Fee</option>
            </select>
          </div>
          {commType !== 'flat_fee' ? (
            <>
              {field('Sale Price', 'sale_price', '$')}
              {field('Rate', 'commission_rate', undefined, '%')}
            </>
          ) : (
            <>{field('Flat Amount', 'flat_fee_amount', '$')}<div /></>
          )}
        </div>

        {/* Row 2: Split */}
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr', gap: 10, marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 10, color: colors.muted, marginBottom: 3 }}>Split</div>
            <select value={splitType} onChange={(e) => setSplitType(e.target.value)} style={{ ...selectStyle, width: '100%' }}>
              <option value="percentage">Percentage</option>
              <option value="flat">Flat</option>
            </select>
          </div>
          {splitType === 'percentage' ? (
            <>
              {field('Agent', 'agent_split_pct', undefined, '%')}
              {field('Broker', 'broker_split_pct', undefined, '%', { disabled: true })}
            </>
          ) : (
            <>
              {field('Agent Amt', 'agent_flat_amount', '$')}
              {field('Broker Amt', 'broker_flat_amount', '$')}
            </>
          )}
        </div>

        {/* Row 3: Fees */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          {field('Transaction Fee', 'transaction_fee', '$')}
          {field('E&O', 'eo_fee', '$')}
          {field('Monthly', 'monthly_fee', '$')}
        </div>
      </SectionCard>

      {/* Disbursement Breakdown */}
      <SectionCard title="Disbursement Breakdown">
        {(() => {
          const rows: { payee: string; description: string; amount: number; highlight?: boolean }[] = [
            { payee: transaction.agent_name || 'Agent', description: `Commission (${agentSplitPct}%)`, amount: agentGross },
            { payee: 'Brokerage', description: brokerGross > rawBrokerGross ? `Broker share ($695 minimum)` : `Broker share (${brokerSplitPct}%)`, amount: brokerGross },
          ];
          if (num('transaction_fee') > 0) rows.push({ payee: 'Brokerage', description: 'Transaction fee', amount: -num('transaction_fee') });
          if (num('eo_fee') > 0) rows.push({ payee: 'E&O Insurance', description: 'E&O fee', amount: -num('eo_fee') });
          if (num('monthly_fee') > 0) rows.push({ payee: 'Brokerage', description: 'Monthly fee', amount: -num('monthly_fee') });
          miscFees.forEach(m => {
            const amt = parseFloat(m.amount) || 0;
            rows.push({ payee: m.paidBy === 'agent' ? 'Agent deduction' : m.paidBy, description: m.description || 'Misc fee', amount: -amt });
          });
          partners.forEach(p => {
            const val = parseFloat(p.amount) || 0;
            const amt = p.isPercent ? grossCommission * (val / 100) : val;
            rows.push({ payee: p.name || 'Partner', description: p.isPercent ? `Referral (${val}%)` : 'Referral fee', amount: -amt });
          });
          rows.push({ payee: transaction.agent_name || 'Agent', description: 'Net pay', amount: agentNet, highlight: true });

          return (
            <div style={{ fontSize: 12 }}>
              {/* Header */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', padding: '6px 0', borderBottom: `2px solid ${colors.navy}`, fontWeight: 700, color: colors.navy, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <span>Payee</span><span>Description</span><span style={{ textAlign: 'right' }}>Amount</span>
              </div>
              {rows.map((r, i) => (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', padding: '6px 0',
                  borderBottom: `1px solid ${colors.border}`,
                  fontWeight: r.highlight ? 700 : 400,
                  color: r.highlight ? colors.green : r.amount < 0 ? colors.red : colors.navy,
                  background: r.highlight ? `${colors.green}08` : 'transparent',
                }}>
                  <span>{r.payee}</span>
                  <span>{r.description}</span>
                  <span style={{ textAlign: 'right' }}>{r.amount < 0 ? `(${fmt(Math.abs(r.amount))})` : fmt(r.amount)}</span>
                </div>
              ))}
            </div>
          );
        })()}
      </SectionCard>

      {/* Partner / Referral Splits */}
      <SectionCard title="Partner Splits">
        {partners.length === 0 && (
          <div style={{ fontSize: 11, color: colors.muted, marginBottom: 8 }}>No referral or co-broke partners added.</div>
        )}
        {partners.map((p, i) => (
          <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 80px 30px', gap: 8, marginBottom: 6, alignItems: 'center' }}>
            <input
              type="text" placeholder="Partner name" value={p.name}
              onChange={(e) => setPartners(prev => prev.map((pp, j) => j === i ? { ...pp, name: e.target.value } : pp))}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 12, fontFamily: fonts.body, color: colors.navy }}
            />
            <input
              type="text" inputMode="decimal" placeholder="0" value={p.amount}
              onChange={(e) => setPartners(prev => prev.map((pp, j) => j === i ? { ...pp, amount: e.target.value } : pp))}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 12, fontFamily: fonts.body, color: colors.navy, textAlign: 'right' }}
            />
            <button
              onClick={() => setPartners(prev => prev.map((pp, j) => j === i ? { ...pp, isPercent: !pp.isPercent } : pp))}
              style={{ padding: '4px 6px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 10, background: colors.offBg, color: colors.navy, cursor: 'pointer' }}
            >{p.isPercent ? '% of gross' : '$ flat'}</button>
            <button
              onClick={() => setPartners(prev => prev.filter((_, j) => j !== i))}
              style={{ padding: '4px', border: 'none', background: 'transparent', color: colors.red, cursor: 'pointer', fontSize: 14 }}
            >&times;</button>
          </div>
        ))}
        <button
          onClick={() => setPartners(prev => [...prev, { id: String(Date.now()), name: '', amount: '', isPercent: true }])}
          style={{ padding: '5px 12px', border: `1px dashed ${colors.gold}`, borderRadius: 4, background: 'transparent', color: colors.gold, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: fonts.body }}
        >+ Add Partner</button>
      </SectionCard>

      {/* Misc Fees */}
      <SectionCard title="Additional Fees">
        {miscFees.length === 0 && (
          <div style={{ fontSize: 11, color: colors.muted, marginBottom: 8 }}>No additional fees. Add staging, photography, or other deal-specific costs.</div>
        )}
        {miscFees.map((m, i) => (
          <div key={m.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 100px 30px', gap: 8, marginBottom: 6, alignItems: 'center' }}>
            <input
              type="text" placeholder="Description" value={m.description}
              onChange={(e) => setMiscFees(prev => prev.map((mm, j) => j === i ? { ...mm, description: e.target.value } : mm))}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 12, fontFamily: fonts.body, color: colors.navy }}
            />
            <input
              type="text" inputMode="decimal" placeholder="0" value={m.amount}
              onChange={(e) => setMiscFees(prev => prev.map((mm, j) => j === i ? { ...mm, amount: e.target.value } : mm))}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 12, fontFamily: fonts.body, color: colors.navy, textAlign: 'right' }}
            />
            <select
              value={m.paidBy}
              onChange={(e) => setMiscFees(prev => prev.map((mm, j) => j === i ? { ...mm, paidBy: e.target.value } : mm))}
              style={{ padding: '4px 6px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 10, background: colors.offBg, color: colors.navy }}
            >
              <option value="agent">Agent</option>
              <option value="broker">Broker</option>
              <option value="buyer">Buyer</option>
              <option value="seller">Seller</option>
            </select>
            <button
              onClick={() => setMiscFees(prev => prev.filter((_, j) => j !== i))}
              style={{ padding: '4px', border: 'none', background: 'transparent', color: colors.red, cursor: 'pointer', fontSize: 14 }}
            >&times;</button>
          </div>
        ))}
        <button
          onClick={() => setMiscFees(prev => [...prev, { id: String(Date.now()), description: '', amount: '', paidBy: 'agent' }])}
          style={{ padding: '5px 12px', border: `1px dashed ${colors.gold}`, borderRadius: 4, background: 'transparent', color: colors.gold, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: fonts.body }}
        >+ Add Fee</button>
      </SectionCard>

      {/* Special Instructions + Table Funding — Broker only */}
      {isBroker && <SectionCard title="Instructions & Settings">
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: colors.muted, marginBottom: 3 }}>Special Instructions</div>
          <textarea
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            placeholder="Any special instructions for escrow or closing..."
            rows={3}
            style={{
              width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`,
              borderRadius: 5, fontSize: 12, fontFamily: fonts.body, color: colors.navy,
              resize: 'vertical', outline: 'none',
            }}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.navy, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={tableFunding}
            onChange={(e) => setTableFunding(e.target.checked)}
            style={{ accentColor: colors.gold }}
          />
          Table Funding — Commission disbursed at closing table
        </label>
      </SectionCard>}

      {/* Approval Status */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 16px', marginBottom: 12,
        background: approvalStatus === 'approved' ? `${colors.green}10` : colors.white,
        border: `1px solid ${approvalStatus === 'approved' ? colors.green : colors.border}`,
        borderRadius: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: approvalStatus === 'approved' ? colors.green : approvalStatus === 'submitted' ? colors.gold : colors.muted,
          }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: colors.navy }}>
            {approvalStatus === 'approved' ? 'CDA Approved' : approvalStatus === 'submitted' ? 'Pending Approval' : 'Draft'}
          </span>
        </div>
        {/* Agent: can submit for approval (draft → submitted) */}
        {!isBroker && approvalStatus === 'draft' && (
          <button
            onClick={async () => {
              await handleSave();
              setApprovalStatus('submitted');
            }}
            style={{
              padding: '5px 16px', border: 'none', borderRadius: 5,
              background: colors.navy, color: colors.white, fontSize: 11,
              fontWeight: 700, cursor: 'pointer', fontFamily: fonts.body,
            }}
          >
            Submit for Approval
          </button>
        )}
        {/* Broker: can approve (submitted → approved) */}
        {isBroker && approvalStatus === 'submitted' && (
          <button
            onClick={async () => {
              await handleSave();
              try {
                await axiosInstance.put(`/transactions/${txnId}/special-instructions`, {
                  special_instructions: specialInstructions,
                  is_table_funding: tableFunding,
                });
              } catch {}
              setApprovalStatus('approved');
            }}
            style={{
              padding: '5px 16px', border: 'none', borderRadius: 5,
              background: colors.green, color: colors.white, fontSize: 11,
              fontWeight: 700, cursor: 'pointer', fontFamily: fonts.body,
            }}
          >
            Approve CDA
          </button>
        )}
        {approvalStatus === 'submitted' && !isBroker && (
          <span style={{ fontSize: 11, color: colors.gold, fontWeight: 600 }}>Awaiting Broker Approval</span>
        )}
        {approvalStatus === 'approved' && (
          <span style={{ fontSize: 11, color: colors.green, fontWeight: 600 }}>Approved & Locked</span>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '8px 20px', border: `1px solid ${colors.border}`, borderRadius: 6,
            background: colors.white, color: colors.navy,
            fontSize: 12, fontWeight: 600, fontFamily: fonts.body,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save Draft'}
        </button>
        {isBroker && <button
          onClick={async () => {
            setGeneratingCDA(true);
            try {
              await handleSave();

              // Fetch brokerage info for PDF
              let brokerageData = { company_name: 'KairosIQ AI Realty', company_address: '', company_phone: '', company_email: '' };
              try {
                const profileRes = await axiosInstance.get('/broker/company-profile');
                if (profileRes.data?.success) {
                  const d = profileRes.data.data?.company_data || profileRes.data.data || {};
                  brokerageData = {
                    company_name: d.company_name || brokerageData.company_name,
                    company_address: d.company_address || '',
                    company_phone: d.company_phone || '',
                    company_email: d.company_email || '',
                  };
                }
              } catch {}

              // Generate PDF client-side
              const blob = await pdf(
                <CDAPDF
                  transaction={transaction}
                  brokerageData={brokerageData}
                  grossCommission={grossCommission}
                  agentGross={agentGross}
                  brokerGross={brokerGross}
                  agentNet={agentNet}
                  agentSplitPct={agentSplitPct}
                  brokerSplitPct={brokerSplitPct}
                  commRate={num('commission_rate')}
                  salePrice={num('sale_price')}
                  txnFee={num('transaction_fee')}
                  eoFee={num('eo_fee')}
                  partners={partners}
                  miscFees={miscFees}
                  specialInstructions={specialInstructions}
                  tableFunding={tableFunding}
                  grossCalc={grossCommission}
                />
              ).toBlob();

              const propertyAddr = transaction?.property?.address || transaction?.property_data?.address || 'Transaction';
              const cleanAddr = propertyAddr.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `CDA_${cleanAddr}_${new Date().toISOString().split('T')[0]}.pdf`;
              a.click();
              URL.revokeObjectURL(url);
            } catch (err) {
              console.error('CDA PDF error:', err);
              alert('Failed to generate CDA PDF');
            }
            setGeneratingCDA(false);
          }}
          disabled={generatingCDA}
          style={{
            padding: '8px 20px', border: 'none', borderRadius: 6,
            background: colors.gold, color: colors.navy,
            fontSize: 12, fontWeight: 700, fontFamily: fonts.body,
            cursor: generatingCDA ? 'not-allowed' : 'pointer',
            opacity: generatingCDA ? 0.6 : 1,
          }}
        >
          {generatingCDA ? 'Generating...' : 'Generate CDA'}
        </button>}
      </div>
    </div>
  );
};

export default CommissionPane;
